import React, { useEffect, useRef } from "react";
import {
  CloudUploadOutline,
  CheckmarkCircleOutline,
  CopyOutline,
  BusinessOutline,
  CashOutline,
  ChevronForward,
  WalletOutline,
} from "@raresail/react-ionicons";
import { App } from "antd";
import { COMPONENT, FORMDATA, TEXT } from "../../constants/constants";
import { API_DEPOSIT_ACCOUNTS, API_MANUAL_DEPOSITS } from "../../constants/apis";
import useGet from "../common/useGet";
import { TSet } from "../common/useSet";
import { showConfetti } from "../../components/elements/ConfettiView";
import { reloadData } from "../../components/lists/ListHandler";
import { InfoBox } from "../../components/elements/Elements";
import { PropOptions } from "../../utils/formUtils";
import { PRIMARY_COLOR, primary10, primary5, PRIMARY_LIGHT } from "../../constants/colors";
import { useWallet } from "../wallets/useWallet";

/* ── Inline file upload component ────────────────────────────── */

function FileUploadField(props: {
  value?: File | null;
  onChange: (f: File | null) => void;
}) {
  const { value, onChange } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const hasFile = !!value;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: 14,
        borderRadius: 12,
        border: "1.5px solid " + (hasFile ? PRIMARY_COLOR : "#e8edf5"),
        background: hasFile ? "rgba(0,51,153,0.03)" : "#fafbfc",
        cursor: "pointer",
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files ? e.target.files[0] : null;
          onChange(f || null);
          e.target.value = "";
        }}
      />
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: hasFile ? "#fff" : PRIMARY_COLOR,
          background: hasFile ? PRIMARY_COLOR : primary10,
          flexShrink: 0,
        }}
      >
        <CloudUploadOutline fontSize={20} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="font13 semiBold textDark">
          {"Proof of Payment "}
          <span className="red">*</span>
        </div>
        {hasFile ? (
          <div className="font11 main semiBold">
            {"✓ " + (value as File).name}
          </div>
        ) : (
          <div className="font11 grey">Upload receipt — PDF, JPG, or PNG</div>
        )}
      </div>
      <button
        type="button"
        onClick={() =>
          hasFile ? onChange(null) : inputRef.current?.click()
        }
        style={{
          padding: "6px 14px",
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
          border: "1.5px solid " + (hasFile ? "#ff4d4f" : PRIMARY_COLOR),
          color: hasFile ? "#ff4d4f" : PRIMARY_COLOR,
          background: "transparent",
        }}
      >
        {hasFile ? "Remove" : "Browse"}
      </button>
    </div>
  );
}

/* ── Copy row helper ─────────────────────────────────────────── */

function CopyRow(props: {
  label: string;
  value: string;
  onCopy: (v: string) => void;
  mono?: boolean;
}) {
  const { label, value, onCopy, mono } = props;
  return (
    <div
      className="flexSpaceCenter"
      style={{ padding: "6px 0", borderBottom: "1px solid #f1f3f8" }}
    >
      <div className="font12">
        <span className="grey">{label}</span>
      </div>
      <div className="flexNullCenter gap8">
        <span
          className={"font13 semiBold textDark" + (mono ? " monospace" : "")}
          style={{ letterSpacing: mono ? "0.5px" : undefined }}
        >
          {value}
        </span>
        <div
          className="pointer animate flexNullCenter"
          onClick={(e) => {
            e.stopPropagation();
            onCopy(value);
          }}
          style={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: primary10,
            color: PRIMARY_COLOR,
          }}
        >
          <CopyOutline fontSize={13} />
        </div>
      </div>
    </div>
  );
}

/* ── Deposit account card ────────────────────────────────────── */

function DepositAccountCard(props: {
  account: any;
  isSelected: boolean;
  onSelect: () => void;
  onCopy: (v: string) => void;
}) {
  const { account, isSelected, onSelect, onCopy } = props;
  const isBank = account.account_type === "bank";

  return (
    <div
      onClick={onSelect}
      className="animate pointer"
      style={{
        borderRadius: 14,
        border: isSelected
          ? "2px solid " + PRIMARY_COLOR
          : "1.5px solid #e8edf5",
        background: isSelected ? primary5 : "#fff",
        overflow: "hidden",
        transition: "all 0.2s ease",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "14px 16px",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: isSelected ? "#fff" : PRIMARY_COLOR,
            background: isSelected ? PRIMARY_COLOR : primary10,
            flexShrink: 0,
            transition: "all 0.2s ease",
          }}
        >
          {isBank ? (
            <BusinessOutline fontSize={20} />
          ) : (
            <CashOutline fontSize={20} />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="font14 semiBold textDark">
            {isBank ? account.bank_name : "Mobile Money"}
          </div>
          <div className="font12 grey" style={{ marginTop: 1 }}>
            {account.account_name}
          </div>
        </div>
        {isSelected ? (
          <CheckmarkCircleOutline fontSize={22} color={PRIMARY_COLOR} />
        ) : (
          <ChevronForward fontSize={16} color="#c0c7d6" />
        )}
      </div>

      {/* Expanded details when selected */}
      {isSelected && (
        <div
          style={{
            padding: "2px 16px 16px",
            borderTop: "1px solid " + PRIMARY_LIGHT,
          }}
        >
          <div
            className="font11 semiBold"
            style={{
              color: PRIMARY_COLOR,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: 4,
              marginTop: 8,
            }}
          >
            Send money to this account
          </div>

          {isBank ? (
            <>
              <CopyRow
                label="Account Number"
                value={account.account_number || ""}
                onCopy={onCopy}
                mono
              />
              <CopyRow
                label="Account Name"
                value={account.account_name || ""}
                onCopy={onCopy}
              />
              {account.branch_name && (
                <CopyRow
                  label="Branch"
                  value={account.branch_name}
                  onCopy={onCopy}
                />
              )}
              <CopyRow
                label="Currency"
                value={account.currency || "UGX"}
                onCopy={onCopy}
              />
            </>
          ) : (
            <>
              <CopyRow
                label="Phone Number"
                value={account.phone_number || ""}
                onCopy={onCopy}
                mono
              />
              <CopyRow
                label="Name"
                value={account.account_name || ""}
                onCopy={onCopy}
              />
              <CopyRow
                label="Provider"
                value={(account.momo_provider || "").replace(/_/g, " ")}
                onCopy={onCopy}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Account selector ────────────────────────────────────────── */

function AccountSelector(props: {
  accounts: any[];
  selected: string;
  onSelect: (id: string) => void;
  loading: boolean;
}) {
  const { accounts, selected, onSelect, loading } = props;
  const { message } = App.useApp();

  const copyText = (text: string) => {
    navigator?.clipboard?.writeText(text);
    message.success("Copied to clipboard!");
  };

  if (loading) {
    return (
      <div className="textCenter p20 grey font12">
        Loading deposit accounts…
      </div>
    );
  }
  if (accounts.length === 0) {
    return (
      <InfoBox color="#ed6c02">
        No deposit accounts available. Contact support.
      </InfoBox>
    );
  }

  return (
    <div className="flexColumn gap10">
      {/* Step indicator */}
      <div className="flexNullCenter gap10" style={{ marginBottom: 2 }}>
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: PRIMARY_COLOR,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          1
        </div>
        <div>
          <div className="font14 semiBold textDark">
            Send money to our account
          </div>
          <div className="font11 grey">
            Tap to view account details, then copy and send via your bank or
            mobile money
          </div>
        </div>
      </div>

      {accounts.map((acc: any) => (
        <DepositAccountCard
          key={acc.id}
          account={acc}
          isSelected={selected === acc.id}
          onSelect={() => onSelect(acc.id)}
          onCopy={copyText}
        />
      ))}
    </div>
  );
}

/* ── Main hook ───────────────────────────────────────────────── */

export const useDepositForm = ({ form, updateFormValues }: any) => {
  const { loadData, data: accountsRaw, loading: loadingAccounts } = useGet();
  const { getWalletBalance } = useWallet();

  useEffect(() => {
    loadData({ api: API_DEPOSIT_ACCOUNTS });
  }, []);

  // Handle both plain array and paginated { results: [] } response
  const accounts: any[] = Array.isArray(accountsRaw)
    ? accountsRaw
    : accountsRaw?.results || [];

  // Auto-select if there's only one account
  useEffect(() => {
    if (accounts.length === 1 && !form?.company_account) {
      updateFormValues({ company_account: accounts[0].id });
    }
  }, [accounts.length]);

  const fields: any[] = [
    {
      type: COMPONENT,
      component: (
        <AccountSelector
          accounts={accounts}
          selected={form?.company_account || ""}
          onSelect={(id: string) => updateFormValues({ company_account: id })}
          loading={loadingAccounts}
        />
      ),
    },

    // Step 2 header
    {
      type: COMPONENT,
      visible: !!form?.company_account,
      component: (
        <div
          className="flexNullCenter gap10"
          style={{ marginTop: 8, marginBottom: -4 }}
        >
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              background: PRIMARY_COLOR,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            2
          </div>
          <div>
            <div className="font14 semiBold textDark">
              Enter deposit details
            </div>
            <div className="font11 grey">
              Tell us how much you sent and upload proof
            </div>
          </div>
        </div>
      ),
    },

    {
      label: "Amount Deposited",
      field: "amount",
      type: TEXT,
      required: true,
      visible: !!form?.company_account,
      props: {
        ...PropOptions.number,
        prefix: "UGX",
        helperText: "Enter the exact amount you sent",
      },
    },
    {
      label: "Reference / Description",
      field: "description",
      type: TEXT,
      visible: !!form?.company_account,
      props: { placeholder: "e.g. Bank ref TXN123456" },
    },
    {
      type: COMPONENT,
      visible: !!form?.company_account,
      component: (
        <FileUploadField
          value={form?.proof_of_payment}
          onChange={(file: File | null) =>
            updateFormValues({ proof_of_payment: file })
          }
        />
      ),
    },
    {
      field: "proof_of_payment",
      type: TEXT,
      visible: false,
      required: true,
      notValid: !form?.proof_of_payment
        ? "Please upload proof of payment"
        : null,
    },
    {
      type: COMPONENT,
      visible: !!form?.company_account,
      component: (
        <InfoBox>
          Your deposit will be verified by our team and credited to your wallet
          (minus any applicable charges). This usually takes 1–2 business hours.
        </InfoBox>
      ),
    },
  ];

  const onSubmit = ({ payload }: any): TSet => {
    const fd = new FormData();
    fd.append("amount", payload.amount);
    if (payload.description) fd.append("description", payload.description);
    if (payload.proof_of_payment)
      fd.append("proof_of_payment", payload.proof_of_payment);
    if (payload.company_account)
      fd.append("company_account", payload.company_account);
    return {
      api: API_MANUAL_DEPOSITS,
      params: fd,
      type: FORMDATA as any,
      successMessage: {
        message:
          "Deposit submitted! We'll verify and credit your wallet shortly.",
        duration: 7,
      },
      onSuccess: () => {
        reloadData("manual_deposits");
        reloadData("transactions");
        showConfetti();
        setTimeout(() => getWalletBalance(), 2000);
      },
    };
  };

  return {
    fields,
    onSubmit,
    title: "Deposit Money",
    submitText: "Submit Deposit",
    allowSubmit:
      !!form?.company_account &&
      !!form?.amount &&
      parseFloat(form?.amount) > 0 &&
      !!form?.proof_of_payment,
  };
};